import { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import SimpleBarReact from "simplebar-react";
import classNames from "classnames";
import { Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";
import { useTranslation } from "react-i18next";

import FormikInput from "../FormikInput";
import SearchInput from "../SearchInput";
import DropdownItem from "../DropdownItem";
import { preventEnterSubmit } from "@helpers/common";

import { useService } from "@/base/hooks/useService";
import { LocationsListsService } from "@services/LocationsListsService";

import styles from "./index.module.scss";

const Placeholder = ({ children }) => (
  <div
    className={classNames(
      styles.placeholder,
      "text-body-1 text-regular text-secondary text-center",
    )}
  >
    {children}
  </div>
);

const FormikCityInput = ({
  flip = false,
  countryCode,
  label = "COUNTRY",
  placeholder = "CITY",
  wrapperClassName,
  name = "city",
  secondName,
  setIsEmpty,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation("placeholders");
  const [cities, setCities] = useState([]);

  const [searchedCities, setSearchedCities] = useState([]);
  const locationsListsService = useService(LocationsListsService);

  const [searchValue, setSearchValue] = useState("");
  const { values, setFieldValue } = useFormikContext();

  const getCities = async () => {
    if (countryCode) {
      const { data } = await locationsListsService.getCities({ countryCode });
      setCities(data);
    }
  };

  useEffect(() => {
    setFieldValue(name, "", true);
    getCities();
  }, [countryCode]);

  const toggle = () => setIsOpen((state) => !state);

  const handleSelect = ({ code, name: name2, country }) => {
    // to update 2 fields in a row we need to wait for the first one to be updated
    setFieldValue(name, name2 || country, true).then(() => {
      if (!secondName) return;
      void setFieldValue(secondName, code);
    });
  };

  const handleSearchChange = ({ target }) => setSearchValue(target.value);
  const handleOnResetClick = () => setSearchValue("");

  useEffect(() => {
    setIsEmpty?.(!cities.length);

    setSearchedCities(
      cities.filter(({ name, country }) => {
        const countryName = name || country;

        return countryName
          .toLocaleLowerCase()
          .startsWith(searchValue.toLocaleLowerCase());
      }),
    );
  }, [cities, searchValue]);

  return (
    <Dropdown
      isOpen={isOpen}
      toggle={toggle}
      className={classNames(styles.dropdown, wrapperClassName)}
    >
      <DropdownToggle
        caret
        className={classNames(
          "btn-resetStyle text-start with-label",
          styles.dropdownToogle,
        )}
      >
        <FormikInput
          label={label}
          placeholder={placeholder}
          name={name}
          wrapperClassName="pe-none"
          readOnly
        />
      </DropdownToggle>
      <DropdownMenu flip={flip} className="w-100 dropdown-menu-input">
        <SimpleBarReact className={styles.scroll}>
          <div className="d-flex w-100 px-3 mb-3">
            <SearchInput
              inputValue={searchValue}
              onChange={handleSearchChange}
              wrapperClassName="w-100 m-0 bg-transparent"
              placeholder="SEARCH"
              onKeyDown={preventEnterSubmit}
              withClearButton
              onResetClick={handleOnResetClick}
              bordered
            />
          </div>
          {!searchedCities.length && (
            <Placeholder>{t("NO_MATCHES")}</Placeholder>
          )}
          {searchedCities.map(({ code, name, country, id }) => (
            <DropdownItem
              key={id}
              name={name || country}
              isSelected={values.countryCode === code}
              onClick={() => handleSelect({ code, name, country, id })}
            />
          ))}
        </SimpleBarReact>
      </DropdownMenu>
    </Dropdown>
  );
};

export default FormikCityInput;
